import offlinePayloadCrypto from './offlineCrypto';
import type { OfflineMetaRecord, OfflineRecordSyncStatus } from './offlineDb';
import offlineDb, { type OfflineDomainRecord } from './offlineDb';

const DOMAIN = 'attendance' as const;

export type AttendanceStoreName =
  | 'employees'
  | 'face_templates'
  | 'shift_templates'
  | 'geofences'
  | 'clock_actions'
  | 'clock_status'
  | 'attendance_stats'
  | 'audit_logs'
  /** Queued attendance violation attempts (offline-first → sync via attendanceViolationSync) */
  | 'violation_reports'
  /** Draft timesheets generated from clock-in data */
  | 'draft_timesheets';

export type AttendanceMetaKey = 'sync_metadata' | 'template_health' | 'clock_status_migrated' | 'face_match_threshold' | 'platform_require_liveness' | 'platform_kiosk_require_liveness';

/** Per-employee clock status stored separately from bulk employee rows (race-safe with MasterSync). */
export type EmployeeClockStatusPayload = {
  id?: number;
  employee_id: number;
  status: 'clocked_in' | 'clocked_out' | 'on_break';
  updated_at: string;
};

interface UpsertRecordOptions {
  parentId?: string;
  status?: string;
  syncStatus?: OfflineRecordSyncStatus;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

function getDb() {
  return offlineDb;
}

function toIso(value?: Date | string | null, fallback?: string): string {
  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  return fallback || new Date().toISOString();
}

function toEntityId(value: string | number | undefined | null): string {
  return String(value ?? '');
}

async function decryptRecordPayload<T>(record: OfflineDomainRecord | undefined): Promise<T | undefined> {
  if (!record) {
    return undefined;
  }

  return offlinePayloadCrypto.decryptValue<T>(record.payload);
}

async function buildRecordRow<T extends { id?: string | number }>(
  store: AttendanceStoreName,
  clientId: number,
  entityId: string,
  item: T,
  existing: OfflineDomainRecord | undefined,
  options?: UpsertRecordOptions,
): Promise<OfflineDomainRecord> {
  const now = toIso(options?.updatedAt);
  const payload = await offlinePayloadCrypto.encryptValue(item);

  return {
    id: existing?.id,
    domain: DOMAIN,
    store,
    client_id: clientId,
    entity_id: entityId,
    parent_id: options?.parentId,
    status: options?.status,
    sync_status: options?.syncStatus || existing?.sync_status || 'synced',
    created_at: existing?.created_at || toIso(options?.createdAt, now),
    updated_at: now,
    payload,
    payload_size: payload.length,
  };
}

function buildMetaStorageKey(clientId: number, key: AttendanceMetaKey): string {
  return `${DOMAIN}:${clientId}:${key}`;
}

async function getStoreRows(store: AttendanceStoreName, clientId: number): Promise<OfflineDomainRecord[]> {
  const db = getDb();
  if (!db) {
    return [];
  }

  return db.records
    .where('[domain+store+client_id]')
    .equals([DOMAIN, store, clientId])
    .toArray();
}

async function getStoreRow(store: AttendanceStoreName, clientId: number, entityId: string): Promise<OfflineDomainRecord | undefined> {
  const db = getDb();
  if (!db) {
    return undefined;
  }

  return db.records
    .where('[domain+store+client_id+entity_id]')
    .equals([DOMAIN, store, clientId, entityId])
    .first();
}

const attendanceStore = {
  async upsertRecord<T extends { id?: string | number }>(
    store: AttendanceStoreName,
    clientId: number,
    entityId: string | number,
    item: T,
    options?: UpsertRecordOptions,
  ): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const normalizedEntityId = toEntityId(entityId);
    const existing = await getStoreRow(store, clientId, normalizedEntityId);
    const row = await buildRecordRow(store, clientId, normalizedEntityId, item, existing, options);
    await db.records.put(row);
  },

  async bulkUpsertRecords<T extends { id?: string | number }>(
    store: AttendanceStoreName,
    clientId: number,
    items: T[],
    getEntityId: (item: T) => string | number,
    getOptions?: (item: T) => UpsertRecordOptions,
  ): Promise<void> {
    const db = getDb();
    if (!db || items.length === 0) {
      return;
    }

    const existingRows = await getStoreRows(store, clientId);
    const existingMap = new Map(existingRows.map((row) => [row.entity_id, row]));
    const rows = await Promise.all(
      items.map((item) => {
        const entityId = toEntityId(getEntityId(item));
        return buildRecordRow(store, clientId, entityId, item, existingMap.get(entityId), getOptions?.(item));
      }),
    );

    await db.records.bulkPut(rows);
  },

  async replaceStoreRecords<T extends { id?: string | number }>(
    store: AttendanceStoreName,
    clientId: number,
    items: T[],
    getEntityId: (item: T) => string | number,
    getOptions?: (item: T) => UpsertRecordOptions,
  ): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const existingRows = await getStoreRows(store, clientId);
    const existingMap = new Map(existingRows.map((row) => [row.entity_id, row]));
    const incomingEntityIds = new Set(items.map((item) => toEntityId(getEntityId(item))));
    const rows = await Promise.all(
      items.map((item) => {
        const entityId = toEntityId(getEntityId(item));
        return buildRecordRow(store, clientId, entityId, item, existingMap.get(entityId), getOptions?.(item));
      }),
    );

    const idsToDelete = existingRows
      .filter((row) => !incomingEntityIds.has(row.entity_id))
      .map((row) => row.id)
      .filter((value): value is number => typeof value === 'number');

    await db.transaction('rw', db.records, async () => {
      if (idsToDelete.length > 0) {
        await db.records.bulkDelete(idsToDelete);
      }
      if (rows.length > 0) {
        await db.records.bulkPut(rows);
      }
    });
  },

  async getRecord<T>(store: AttendanceStoreName, clientId: number, entityId: string | number): Promise<T | undefined> {
    const row = await getStoreRow(store, clientId, toEntityId(entityId));
    return decryptRecordPayload<T>(row);
  },

  async listRecords<T>(store: AttendanceStoreName, clientId: number): Promise<T[]> {
    const rows = await getStoreRows(store, clientId);
    const settled = await Promise.allSettled(rows.map((row) => decryptRecordPayload<T>(row)));
    return settled.reduce<T[]>((accumulator, result) => {
      if (result.status === 'fulfilled' && result.value !== undefined) {
        accumulator.push(result.value);
      } else if (result.status === 'rejected') {
        console.warn(`[attendanceStore] Skipping undecryptable ${store} record for client ${clientId}`, result.reason);
      }
      return accumulator;
    }, []);
  },

  async listRecordsByParent<T>(store: AttendanceStoreName, clientId: number, parentId: string | number): Promise<T[]> {
    const db = getDb();
    if (!db) {
      return [];
    }

    const rows = await db.records
      .where('[domain+store+client_id+parent_id]')
      .equals([DOMAIN, store, clientId, toEntityId(parentId)])
      .toArray();
    const settled = await Promise.allSettled(rows.map((row) => decryptRecordPayload<T>(row)));
    return settled.reduce<T[]>((accumulator, result) => {
      if (result.status === 'fulfilled' && result.value !== undefined) {
        accumulator.push(result.value);
      } else if (result.status === 'rejected') {
        console.warn(`[attendanceStore] Skipping undecryptable ${store} record for client ${clientId} parent ${parentId}`, result.reason);
      }
      return accumulator;
    }, []);
  },

  async filterRecords<T>(
    store: AttendanceStoreName,
    clientId: number,
    predicate: (item: T) => boolean,
  ): Promise<T[]> {
    const items = await this.listRecords<T>(store, clientId);
    return items.filter(predicate);
  },

  async countRecords(
    store: AttendanceStoreName,
    clientId: number,
    predicate?: (item: unknown) => boolean,
  ): Promise<number> {
    const db = getDb();
    if (!db) {
      return 0;
    }

    if (!predicate) {
      return db.records.where('[domain+store+client_id]').equals([DOMAIN, store, clientId]).count();
    }

    const items = await this.listRecords<unknown>(store, clientId);
    return items.filter(predicate).length;
  },

  async countRecordsByStatus(store: AttendanceStoreName, clientId: number, status: string): Promise<number> {
    const db = getDb();
    if (!db) {
      return 0;
    }

    return db.records
      .where('[domain+store+client_id+status]')
      .equals([DOMAIN, store, clientId, status])
      .count();
  },

  async getStorePayloadSize(store: AttendanceStoreName, clientId: number): Promise<number> {
    const rows = await getStoreRows(store, clientId);
    return rows.reduce((total, row) => total + (row.payload_size || 0), 0);
  },

  async deleteRecord(store: AttendanceStoreName, clientId: number, entityId: string | number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const row = await getStoreRow(store, clientId, toEntityId(entityId));
    if (row?.id) {
      await db.records.delete(row.id);
    }
  },

  async deleteRecordsWhere<T>(
    store: AttendanceStoreName,
    clientId: number,
    predicate: (item: T) => boolean,
  ): Promise<number> {
    const db = getDb();
    if (!db) {
      return 0;
    }

    const rows = await getStoreRows(store, clientId);
    const idsToDelete: number[] = [];

    for (const row of rows) {
      const item = await decryptRecordPayload<T>(row);
      if (item !== undefined && predicate(item) && typeof row.id === 'number') {
        idsToDelete.push(row.id);
      }
    }

    if (idsToDelete.length > 0) {
      await db.records.bulkDelete(idsToDelete);
    }

    return idsToDelete.length;
  },

  async clearStore(store: AttendanceStoreName, clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const rows = await getStoreRows(store, clientId);
    const ids = rows.map((row) => row.id).filter((value): value is number => typeof value === 'number');
    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },

  async clearClient(clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const [recordRows, metaRows] = await Promise.all([
      db.records.where('[domain+client_id]').equals([DOMAIN, clientId]).toArray(),
      db.meta.where('[domain+client_id]').equals([DOMAIN, clientId]).toArray(),
    ]);

    const recordIds = recordRows.map((row) => row.id).filter((value): value is number => typeof value === 'number');
    const metaKeys = metaRows.map((row) => row.key);

    await db.transaction('rw', db.records, db.meta, async () => {
      if (recordIds.length > 0) {
        await db.records.bulkDelete(recordIds);
      }
      if (metaKeys.length > 0) {
        await db.meta.bulkDelete(metaKeys);
      }
    });
  },

  async setMeta<T>(clientId: number, key: AttendanceMetaKey, value: T): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const row: OfflineMetaRecord = {
      key: buildMetaStorageKey(clientId, key),
      domain: DOMAIN,
      client_id: clientId,
      updated_at: new Date().toISOString(),
      payload: await offlinePayloadCrypto.encryptValue(value),
    };

    await db.meta.put(row);
  },

  async getMeta<T>(clientId: number, key: AttendanceMetaKey): Promise<T | null> {
    const db = getDb();
    if (!db) {
      return null;
    }

    const row = await db.meta.get(buildMetaStorageKey(clientId, key));
    if (!row) {
      return null;
    }

    return offlinePayloadCrypto.decryptValue<T>(row.payload);
  },

  async deleteMeta(clientId: number, key: AttendanceMetaKey): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    await db.meta.delete(buildMetaStorageKey(clientId, key));
  },

  async setClockStatus(
    clientId: number,
    employeeId: number | string,
    status: EmployeeClockStatusPayload['status'],
  ): Promise<void> {
    const eid = Number(employeeId);
    const payload: EmployeeClockStatusPayload = {
      id: eid,
      employee_id: eid,
      status,
      updated_at: new Date().toISOString(),
    };
    await this.upsertRecord('clock_status', clientId, String(eid), payload, {
      status,
      syncStatus: 'synced',
    });
  },

  async getClockStatus(
    clientId: number,
    employeeId: number | string,
  ): Promise<EmployeeClockStatusPayload | null> {
    const row = await this.getRecord<EmployeeClockStatusPayload>(
      'clock_status',
      clientId,
      String(employeeId),
    );
    return row ?? null;
  },

  /** Map employeeId string → status (for MasterSync merge). */
  async getAllClockStatusesMap(clientId: number): Promise<Map<string, string>> {
    const rows = await getStoreRows('clock_status', clientId);
    const map = new Map<string, string>();
    for (const row of rows) {
      try {
        const p = await decryptRecordPayload<EmployeeClockStatusPayload>(row);
        if (p?.status) {
          map.set(String(p.employee_id), p.status);
        }
      } catch (err) {
        console.warn(`[attendanceStore] Skipping undecryptable clock_status record for client ${clientId}`, err);
      }
    }
    return map;
  },
};

export default attendanceStore;
